import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Logo, BRAND } from "@/components/Brand";
import {
  ArrowRight, Check, CalendarDays, Zap, Users, History, CalendarOff,
  FileText, Sparkles, Mail, Phone, Shield, ChevronRight,
} from "lucide-react";

function GradientBg() {
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-500/20 blur-3xl" />
      <div className="absolute top-32 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-500/20 blur-3xl" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-blue-400/10 blur-3xl" />
    </div>
  );
}

function NavBar({ workshop }) {
  return (
    <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <Logo size={36} />
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-lg">{BRAND.name}</div>
            <div className="text-[11px] text-slate-400 hidden sm:block">{BRAND.domain}</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
          <a href="#funciones" className="hover:text-white">Funciones</a>
          <a href="#pasos" className="hover:text-white">Cómo funciona</a>
          <a href="#precios" className="hover:text-white">Precios</a>
          <a href="#faq" className="hover:text-white">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          {workshop ? (
            <Link to="/panel">
              <Button className="bg-blue-600 hover:bg-blue-700 h-10" data-testid="nav-go-panel">
                Ir a mi taller
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/entrar" className="hidden sm:inline">
                <Button variant="ghost" className="text-slate-200 hover:bg-white/10 h-10" data-testid="nav-login">
                  Entrar
                </Button>
              </Link>
              <Link to="/entrar?tab=register">
                <Button className="bg-blue-600 hover:bg-blue-700 h-10" data-testid="nav-register">
                  Empieza gratis
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero({ workshop }) {
  const navigate = useNavigate();
  return (
    <section className="relative">
      <GradientBg />
      <div className="max-w-6xl mx-auto px-4 pt-16 pb-20 sm:pt-24 sm:pb-28 grid md:grid-cols-2 gap-10 items-center">
        <div className="space-y-6">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider px-3 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-300">
            <Sparkles className="w-3.5 h-3.5" /> 3 meses gratis · sin tarjeta
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
            La agenda que <span className="text-blue-400">no colapsa</span> tu taller.
          </h1>
          <p className="text-lg text-slate-300 max-w-lg">
            Programa reparaciones con la <b className="text-white">regla 60/40</b>: llena el 60% del día y deja un 40% libre para
            los coches que llegan sin cita. Deja de improvisar.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => navigate(workshop ? "/panel" : "/entrar?tab=register")}
              className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-base"
              data-testid="hero-cta-primary"
            >
              {workshop ? "Ir a mi taller" : "Empieza gratis 90 días"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="#funciones">
              <Button variant="outline" className="h-12 px-6 border-white/20 bg-white/5 hover:bg-white/10 text-white text-base w-full sm:w-auto">
                Ver funciones
              </Button>
            </a>
          </div>
          <div className="flex items-center gap-4 pt-2 text-xs text-slate-400">
            <div className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400"/> Sin permanencia</div>
            <div className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400"/> Instalable en móvil</div>
            <div className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-emerald-400"/> Datos en la nube</div>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/40 to-indigo-600/40 blur-2xl" />
          <div className="relative rounded-3xl bg-slate-900 border border-white/10 p-4 sm:p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
              <div className="ml-3 text-xs text-slate-400 truncate">citastallerapp.com/panel</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              {[{ d: "Lun", u: 4.5, s: 6 }, { d: "Mar", u: 6, s: 6, full: true }, { d: "Mié", u: 3.5, s: 6 }, { d: "Jue", u: 2, s: 6 }, { d: "Vie", u: 5, s: 6 }, { d: "Sáb", u: 0, s: 0 }].map((day, i) => (
                <div key={i} className={`rounded-xl p-3 border ${day.full ? "bg-red-500/10 border-red-500/30" : day.s === 0 ? "bg-slate-800 border-slate-700 opacity-50" : "bg-blue-500/10 border-blue-500/30"}`}>
                  <div className="text-[11px] uppercase text-slate-400">{day.d}</div>
                  <div className="text-lg font-display font-bold text-white mt-0.5">
                    {day.s === 0 ? "—" : `${day.u}/${day.s}h`}
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full ${day.full ? "bg-red-400" : "bg-blue-400"}`}
                      style={{ width: `${day.s ? (day.u / day.s) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl border border-white/10 bg-slate-800/60 p-3 text-sm text-slate-300 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-400" />
              Mecánica rápida: 2 huecos disponibles hoy
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Steps() {
  const steps = [
    { n: 1, title: "Registra tu taller", desc: "Crea la cuenta con email o Google. Elige el nombre de tu taller y listo." },
    { n: 2, title: "Añade a tus mecánicos", desc: "Configura horas diarias, general o rápida. La app calcula la capacidad total." },
    { n: 3, title: "Agenda sin colapsar", desc: "La regla 60/40 reserva un 40% del día para imprevistos. Si no cabe, va al día siguiente." },
  ];
  return (
    <section id="pasos" className="relative py-20 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Cómo funciona</h2>
          <p className="text-slate-400">De 0 a agenda inteligente en menos de 5 minutos.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {steps.map((s) => (
            <div key={s.n} className="rounded-2xl bg-slate-900/60 border border-white/10 p-6" data-testid={`step-${s.n}`}>
              <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-display font-bold flex items-center justify-center mb-4">{s.n}</div>
              <h3 className="font-display text-xl font-bold text-white">{s.title}</h3>
              <p className="text-slate-400 mt-2 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { icon: CalendarDays, title: "Agenda con regla 60/40", desc: "Configura el porcentaje que quieras. La capacidad se recalcula automáticamente cada día." },
  { icon: Zap, title: "Mecánica rápida", desc: "Aparte de la agenda general. Ideal para revisiones, cambios de aceite y trabajos cortos sin cita." },
  { icon: History, title: "Historial por matrícula", desc: "Escribe la matrícula y ve todas las reparaciones anteriores del vehículo al instante." },
  { icon: Users, title: "Mecánicos y horarios", desc: "Cada mecánico con sus horas. General o rápida. La capacidad total sale sola." },
  { icon: CalendarOff, title: "Vacaciones y festivos", desc: "Bloquea días festivos del taller y las vacaciones individuales de cada mecánico." },
  { icon: FileText, title: "Agenda semanal en PDF", desc: "Imprime la planificación de la semana en un clic para ponerla en el taller." },
];

function Features() {
  return (
    <section id="funciones" className="py-20 border-t border-white/5 bg-slate-950">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Todo lo que necesitas para no perder coches</h2>
          <p className="text-slate-400">Diseñado con talleres reales. Sin funciones que estorban.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="group rounded-2xl bg-slate-900/60 border border-white/10 p-6 hover:border-blue-400/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-blue-500/15 border border-blue-400/30 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-blue-300" />
                </div>
                <h3 className="font-display text-lg font-bold text-white">{f.title}</h3>
                <p className="text-slate-400 mt-2 text-sm">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Pricing({ workshop }) {
  const navigate = useNavigate();
  const cta = workshop ? "/panel" : "/entrar?tab=register";
  const ctaText = workshop ? "Ir a mi taller" : "Empezar prueba gratis";
  return (
    <section id="precios" className="py-20 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Un precio simple</h2>
          <p className="text-slate-400">Prueba todo durante 3 meses. Si te encaja, sigues por menos de 3 € al mes.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12 max-w-4xl mx-auto">
          <div className="rounded-2xl bg-slate-900/60 border border-white/10 p-8" data-testid="pricing-free">
            <div className="text-xs uppercase tracking-wider text-slate-400">Gratis</div>
            <div className="mt-2 font-display text-4xl font-bold text-white">0€ <span className="text-lg text-slate-400 font-normal">/ para siempre</span></div>
            <p className="text-slate-400 text-sm mt-2">Cuando termina la prueba de 90 días, tu cuenta pasa al plan gratuito.</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              {["1 mecánico", "Sin vacaciones ni festivos", "Máximo 3 citas al día", "Historial por matrícula ilimitado"].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <span className="mt-1 inline-block w-1.5 h-1.5 rounded-full bg-slate-500" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-gradient-to-b from-blue-600/25 to-slate-900/60 border border-blue-400/40 p-8 shadow-xl shadow-blue-500/10" data-testid="pricing-paid">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-blue-300">Anual · recomendado</div>
              <div className="text-[11px] uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/40">3 meses gratis</div>
            </div>
            <div className="mt-2 font-display text-4xl font-bold text-white">42,35€ <span className="text-lg text-slate-300 font-normal">/ año</span></div>
            <p className="text-slate-300 text-sm mt-2">35 € + 21 % IVA. Menos que un café al mes. Sin permanencia.</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-100">
              {["Mecánicos ilimitados (general y rápida)", "Vacaciones por mecánico", "Días festivos personalizados", "Citas diarias ilimitadas", "Agenda semanal en PDF", "Cancela desde el portal Stripe"].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <Button onClick={() => navigate(cta)} className="mt-6 w-full h-12 bg-blue-600 hover:bg-blue-700 text-base" data-testid="pricing-cta">
              {ctaText}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
        <p className="text-center text-xs text-slate-500 mt-6 flex items-center justify-center gap-2">
          <Shield className="w-3.5 h-3.5" /> Pago seguro con Stripe · Tus datos siempre viajan cifrados.
        </p>
      </div>
    </section>
  );
}

const FAQS = [
  { q: "¿Qué es la regla 60/40 y por qué la necesito?", a: "Programas el 60% de las horas totales de tus mecánicos. El 40% restante queda libre para los coches que llegan sin cita ese día. Puedes cambiar el porcentaje cuando quieras desde Ajustes." },
  { q: "¿Qué pasa si excedo los límites del plan gratuito?", a: "La app te muestra un aviso amable y te ofrece suscribirte. Nada se borra: si te suscribes, todo vuelve a funcionar sin tocar tus datos." },
  { q: "¿Puedo cancelar cuando quiera?", a: "Sí. Desde /suscripción → Gestionar suscripción abres el portal de Stripe y cancelas en un clic. Mantienes el acceso hasta el final del período pagado." },
  { q: "¿Funciona en el móvil?", a: "Sí, la app es responsive e instalable como PWA (icono en pantalla de inicio). Muy pronto también estará en Google Play." },
  { q: "¿Qué pasa con mis datos si dejo de pagar?", a: "Nunca se borran. Se congelan las funciones premium, pero tus reparaciones, matrículas e historial siguen ahí." },
  { q: "¿Emitís factura?", a: "Sí. Stripe genera automáticamente el recibo tras cada pago y lo envía al email del taller." },
];

function FAQ() {
  return (
    <section id="faq" className="py-20 border-t border-white/5 bg-slate-950">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Preguntas frecuentes</h2>
        </div>
        <div className="mt-10 space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="rounded-xl bg-slate-900/60 border border-white/10 p-5 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer list-none flex items-center justify-between text-white font-display font-semibold">
                {f.q}
                <ChevronRight className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-90" />
              </summary>
              <p className="text-slate-400 text-sm mt-3">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA({ workshop }) {
  const navigate = useNavigate();
  return (
    <section className="py-20 border-t border-white/5">
      <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
          Deja de perder coches por falta de agenda
        </h2>
        <p className="text-slate-400 max-w-xl mx-auto">
          3 meses gratis. Sin tarjeta. Empieza en menos de 5 minutos.
        </p>
        <Button
          onClick={() => navigate(workshop ? "/panel" : "/entrar?tab=register")}
          className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-lg"
          data-testid="cta-bottom"
        >
          {workshop ? "Ir a mi taller" : "Empieza gratis ahora"}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-slate-950 py-10 text-slate-400 text-sm">
      <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Logo size={32} />
            <div className="font-display font-bold text-white">{BRAND.name}</div>
          </div>
          <p className="text-xs text-slate-500 max-w-xs">{BRAND.tagline}. Un producto pensado para talleres, hecho con talleres.</p>
        </div>
        <div>
          <div className="text-white font-semibold mb-3">Contacto</div>
          <ul className="space-y-2 text-xs">
            <li className="flex items-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              <a href={`mailto:${BRAND.contactEmail}`} className="hover:text-white" data-testid="footer-email">{BRAND.contactEmail}</a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5" />
              <a href={`tel:${BRAND.contactPhoneIntl}`} className="hover:text-white" data-testid="footer-phone">{BRAND.contactPhone}</a>
            </li>
          </ul>
        </div>
        <div>
          <div className="text-white font-semibold mb-3">Producto</div>
          <ul className="space-y-2 text-xs">
            <li><a href="#funciones" className="hover:text-white">Funciones</a></li>
            <li><a href="#precios" className="hover:text-white">Precios</a></li>
            <li><a href="#faq" className="hover:text-white">FAQ</a></li>
            <li><Link to="/entrar" className="hover:text-white">Entrar</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-semibold mb-3">Legal</div>
          <ul className="space-y-2 text-xs">
            <li><Link to="/aviso-legal" className="hover:text-white" data-testid="footer-legal-aviso">Aviso legal</Link></li>
            <li><Link to="/privacidad" className="hover:text-white" data-testid="footer-legal-privacidad">Política de privacidad</Link></li>
            <li><Link to="/cookies" className="hover:text-white" data-testid="footer-legal-cookies">Política de cookies</Link></li>
            <li><Link to="/terminos" className="hover:text-white" data-testid="footer-legal-terminos">Términos de uso</Link></li>
            <li><Link to="/contratacion" className="hover:text-white" data-testid="footer-legal-contratacion">Condiciones de contratación</Link></li>
            <li><Link to="/eliminar-cuenta" className="hover:text-white" data-testid="footer-legal-eliminar">Eliminar mi cuenta</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
        <div>© {new Date().getFullYear()} {BRAND.name} · {BRAND.domain}</div>
        <div className="opacity-70">Hecho para talleres 🇪🇸</div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const { workshop } = useAuth();
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="landing-page">
      <NavBar workshop={workshop || null} />
      <Hero workshop={workshop || null} />
      <Steps />
      <Features />
      <Pricing workshop={workshop || null} />
      <FAQ />
      <CTA workshop={workshop || null} />
      <Footer />
    </div>
  );
}
